import { BugReportModal } from '@/components/global/bug-reports/BugReportModal';
import { GlobalSearch } from '@/components/global/GlobalSearch';
import { NotificationSidebar } from '@/components/global/notifications/NotificationSidebar';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';
import { MobileNavigationDrawer } from '@/components/layout/MobileNavigationDrawer';
import { useNavGroups } from '@/components/layout/nav-config';
import { UserMenuContent } from '@/components/layout/UserMenuContent';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/shadcn/avatar';
import { Button } from '@/components/ui/shadcn/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/shadcn/dropdown-menu';
import { SidebarTrigger } from '@/components/ui/shadcn/sidebar';
import { useInitials } from '@/hooks/use-initials';
import { useIsMobile } from '@/hooks/use-mobile';
import { User, type BreadcrumbItem, type NavGroup, type SharedData } from '@/types';
import { usePage } from '@inertiajs/react';
import { Bell, Search } from 'lucide-react';
import { useEffect, useState } from 'react';

interface AppHybridHeaderProps {
    breadcrumbs?: BreadcrumbItem[];
}

export function AppHybridHeader({ breadcrumbs = [] }: AppHybridHeaderProps) {
    const page = usePage<SharedData>();
    const { auth } = page.props;
    const getInitials = useInitials();
    const isMobile = useIsMobile();
    const [unread, setUnread] = useState<number>(0);
    const [notificationSidebarOpen, setNotificationSidebarOpen] = useState<boolean>(false);
    const [searchOpen, setSearchOpen] = useState<boolean>(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const { sections, hiddenGroup } = useNavGroups();
    const mainNavItems: NavGroup[] = hiddenGroup ? [...sections, hiddenGroup] : sections;

    const refreshNotificationCounts = () => {
        fetch('/notifications/counts', { headers: { Accept: 'application/json' } })
            .then((r) => r.json())
            .then((data) => setUnread(Number(data?.unread || 0)))
            .catch(() => {});
    };

    useEffect(() => {
        refreshNotificationCounts();

        const userId = page.props.auth?.user?.id;
        if (window.Echo && userId) {
            window.Echo.private(`private-user.${userId}`).listen('NotificationCreated', () => {
                setUnread((n) => n + 1);
            });
        }

        // Listen for notification updates
        const handleNotificationUpdate = () => {
            refreshNotificationCounts();
        };
        window.addEventListener('notifications-updated', handleNotificationUpdate);

        return () => {
            window.removeEventListener('notifications-updated', handleNotificationUpdate);
        };
    }, [page.props.auth?.user?.id]);

    return (
        <>
            <header className="border-sidebar-border flex h-16 shrink-0 items-center gap-2 border-b px-4 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12 md:px-6">
                <div className="flex w-full items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        {!isMobile && <SidebarTrigger className="-ml-1" />}
                        {isMobile && (
                            <MobileNavigationDrawer
                                open={isMobileMenuOpen}
                                onOpenChange={setIsMobileMenuOpen}
                                navItems={mainNavItems}
                                user={auth.user as User}
                            />
                        )}
                        <Breadcrumbs breadcrumbs={breadcrumbs} />
                    </div>

                    <div className="flex items-center space-x-2">
                        <BugReportModal />
                        <Button variant="ghost" size="icon" className="group h-9 w-9 cursor-pointer" onClick={() => setSearchOpen(true)}>
                            <Search className="!size-5 opacity-80 group-hover:opacity-100" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="group relative h-9 w-9 cursor-pointer"
                            onClick={() => setNotificationSidebarOpen(true)}
                        >
                            <Bell className="!size-5 opacity-80 group-hover:opacity-100" />
                            {unread > 0 && (
                                <span className="bg-primary text-primary-foreground absolute -top-1 -right-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-medium">
                                    {unread}
                                </span>
                            )}
                        </Button>

                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="size-10 rounded-full p-1">
                                    <Avatar className="size-8 overflow-hidden rounded-full">
                                        <AvatarImage
                                            src={(auth.user as User).avatar || ''}
                                            alt={String((auth.user as User).first_name + ' ' + (auth.user as User).last_name || '')}
                                        />
                                        <AvatarFallback className="bg-muted text-muted-foreground rounded-lg">
                                            {getInitials(String((auth.user as User).first_name + ' ' + (auth.user as User).last_name || ''))}
                                        </AvatarFallback>
                                    </Avatar>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="w-56" align="end">
                                <UserMenuContent user={auth.user as User} />
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            </header>

            <NotificationSidebar open={notificationSidebarOpen} onOpenChange={setNotificationSidebarOpen} />
            <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
        </>
    );
}
